import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import {
  getUser,
  listAllStrategies,
  resetPassword,
  setBrokerPolicies,
  setStrategyGrants,
  updateUser,
  type AdminUserDetail,
  type BrokerPolicyIn,
} from "../../api/admin";
import { DATA_ONLY_BROKERS } from "../../api/brokers";
import { apiErrorMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import Button from "../../components/Button";
import Card from "../../components/Card";
import Chip from "../../components/Chip";
import { Tabs } from "../../components/Tabs";
import UserTradingPanel from "./UserTradingPanel";
import { Field, Toggle } from "./UsersPage";

export default function AdminUserDetailPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const { user: me } = useAuth();

  const q = useQuery({
    queryKey: ["admin", "user", id],
    queryFn: () => getUser(id),
    enabled: !!id,
  });
  const strategies = useQuery({
    queryKey: ["admin", "strategies"],
    queryFn: listAllStrategies,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["admin", "user", id] });
    void qc.invalidateQueries({ queryKey: ["admin", "users"] });
  };

  if (q.isLoading) return <div className="text-sm text-muted">Loading…</div>;
  if (q.isError)
    return <div className="text-sm text-down-600 dark:text-down-400">{apiErrorMessage(q.error)}</div>;
  const user = q.data!;
  const isSelf = me?.id === user.id;

  return (
    <div className="space-y-5">
      <div>
        <Link
          to="/admin/users"
          className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-fg"
        >
          <ArrowLeft size={14} /> All users
        </Link>
        <h1 className="text-xl font-semibold mt-2 flex items-center gap-2">
          {user.email}
          {user.is_admin && <Chip tone="brand">admin</Chip>}
          {!user.is_active && <Chip tone="down">inactive</Chip>}
        </h1>
      </div>

      <Tabs
        tabs={[
          { key: "trading", label: "Trading" },
          { key: "settings", label: "Settings" },
        ]}
      >
        {(active) =>
          active === "trading" ? (
            <div>
              <UserTradingPanel user={user} />
            </div>
          ) : (
            <div className="space-y-5">
              <ProfileCard user={user} isSelf={isSelf} onSaved={invalidate} />
              <FeaturesCard user={user} onSaved={invalidate} />
              <BrokerAccessCard user={user} onSaved={invalidate} />
              <StrategyGrantsCard
                user={user}
                all={(strategies.data ?? []).map((s) => ({
                  key: s.key,
                  label: s.display_name,
                }))}
                onSaved={invalidate}
              />
            </div>
          )
        }
      </Tabs>
    </div>
  );
}

function ProfileCard({
  user,
  isSelf,
  onSaved,
}: {
  user: AdminUserDetail;
  isSelf: boolean;
  onSaved: () => void;
}) {
  const [displayName, setDisplayName] = useState(user.display_name ?? "");
  const [pw, setPw] = useState("");
  const [pwDone, setPwDone] = useState(false);

  const save = useMutation({
    mutationFn: (p: Parameters<typeof updateUser>[1]) => updateUser(user.id, p),
    onSuccess: onSaved,
  });
  const reset = useMutation({
    mutationFn: () => resetPassword(user.id, pw),
    onSuccess: () => {
      setPw("");
      setPwDone(true);
    },
  });

  return (
    <Card title="Profile">
      <div className="space-y-4">
        <div className="flex items-end gap-3">
          <div className="w-72">
            <Field label="Display name">
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full rounded border border-border bg-surface px-2.5 py-1.5 text-sm"
              />
            </Field>
          </div>
          <Button
            size="sm"
            disabled={save.isPending || displayName === (user.display_name ?? "")}
            onClick={() => save.mutate({ display_name: displayName || null })}
          >
            Save
          </Button>
        </div>

        <div className="space-y-2">
          {/* These two apply immediately rather than staging behind a Save —
              deactivating someone is an action, not an edit. The status line
              makes that legible; without it the card looked inert next to the
              ones that do have a Save button. */}
          <div className="h-4 text-xs">
            {save.isPending && <span className="text-muted">Saving…</span>}
            {!save.isPending && save.isSuccess && (
              <span className="text-up-600 dark:text-up-400">Saved.</span>
            )}
          </div>
          <Toggle
            checked={user.is_admin}
            disabled={isSelf}
            onChange={(v) => save.mutate({ is_admin: v })}
            label="Administrator"
          >
            {isSelf
              ? "You cannot change your own admin status."
              : "Can manage users and limits. Never sees other users' trading data."}
          </Toggle>
          <Toggle
            checked={user.is_active}
            disabled={isSelf}
            onChange={(v) => save.mutate({ is_active: v })}
            label="Active"
          >
            {isSelf
              ? "You cannot deactivate yourself."
              : "Deactivating signs the user out immediately and blocks login."}
          </Toggle>
        </div>

        <div className="flex items-end gap-3 pt-1 border-t border-border">
          <div className="w-72 pt-3">
            <Field label="Set a new password" hint="Minimum 8 characters.">
              <input
                type="password"
                value={pw}
                onChange={(e) => {
                  setPw(e.target.value);
                  setPwDone(false);
                }}
                className="w-full rounded border border-border bg-surface px-2.5 py-1.5 text-sm"
              />
            </Field>
          </div>
          <Button
            size="sm"
            variant="secondary"
            disabled={pw.length < 8 || reset.isPending}
            onClick={() => reset.mutate()}
          >
            Reset password
          </Button>
          {pwDone && <span className="text-xs text-up-600 dark:text-up-400 pb-2">Password updated.</span>}
        </div>

        {(save.error || reset.error) && (
          <div className="text-sm text-down-600 dark:text-down-400">
            {apiErrorMessage(save.error ?? reset.error)}
          </div>
        )}
      </div>
    </Card>
  );
}

function FeaturesCard({
  user,
  onSaved,
}: {
  user: AdminUserDetail;
  onSaved: () => void;
}) {
  // Staged edits + an explicit Save, matching Broker access and Strategy
  // access. These previously saved the instant you flicked a toggle, which
  // worked but gave no confirmation and read as broken next to the two cards
  // below that DO have a Save button.
  const [backtest, setBacktest] = useState(user.can_backtest);
  const [backfill, setBackfill] = useState(user.can_backfill);
  useEffect(() => {
    setBacktest(user.can_backtest);
    setBackfill(user.can_backfill);
  }, [user.can_backtest, user.can_backfill]);

  const save = useMutation({
    mutationFn: () =>
      updateUser(user.id, { can_backtest: backtest, can_backfill: backfill }),
    onSuccess: onSaved,
  });

  const dirty =
    backtest !== user.can_backtest || backfill !== user.can_backfill;

  return (
    <Card
      title="Features"
      actions={
        <div className="flex items-center gap-2">
          {dirty && <span className="text-xs text-muted">Unsaved changes</span>}
          {!dirty && save.isSuccess && (
            <span className="text-xs text-up-600 dark:text-up-400">Saved.</span>
          )}
          <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="space-y-2">
        <Toggle checked={backtest} onChange={setBacktest} label="Backtesting">
          Read-only against stored bars. Runs share a concurrency pool with other
          users, capped per user so one big sweep can't starve everyone else.
        </Toggle>
        <Toggle checked={backfill} onChange={setBackfill} label="Data backfill">
          Shows the <strong>Data</strong> page for bulk backfills, which write to
          the shared market-data store every user reads from. Not needed to
          download a symbol from the Chart — every user can do that through
          their own broker connection.
        </Toggle>
        {save.error && (
          <div className="text-sm text-down-600 dark:text-down-400">{apiErrorMessage(save.error)}</div>
        )}
      </div>
    </Card>
  );
}

function BrokerAccessCard({
  user,
  onSaved,
}: {
  user: AdminUserDetail;
  onSaved: () => void;
}) {
  const [rows, setRows] = useState<BrokerPolicyIn[]>(
    user.broker_policies.map((p) => ({
      broker: p.broker,
      enabled: p.enabled,
      max_accounts: p.max_accounts,
    })),
  );
  useEffect(() => {
    setRows(
      user.broker_policies.map((p) => ({
        broker: p.broker,
        enabled: p.enabled,
        max_accounts: p.max_accounts,
      })),
    );
  }, [user.broker_policies]);

  const save = useMutation({
    mutationFn: () => setBrokerPolicies(user.id, rows),
    onSuccess: onSaved,
  });

  const usedByBroker = Object.fromEntries(
    user.broker_policies.map((p) => [p.broker, p.used]),
  );
  const dirty = JSON.stringify(rows) !== JSON.stringify(
    user.broker_policies.map((p) => ({
      broker: p.broker,
      enabled: p.enabled,
      max_accounts: p.max_accounts,
    })),
  );
  const stranding = rows.filter(
    (r) => (usedByBroker[r.broker] ?? 0) > (r.enabled ? r.max_accounts : 0),
  );

  return (
    <Card
      title="Broker access"
      subtitle="Which brokers this user may connect, and how many accounts of each. A data-only broker can backfill history but cannot place orders, so a user with only those cannot deploy."
      actions={
        <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      }
    >
      <div className="space-y-2">
        {rows.map((r, i) => {
          const used = usedByBroker[r.broker] ?? 0;
          const overCap = used > (r.enabled ? r.max_accounts : 0);
          return (
            <div key={r.broker} className="flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={r.enabled}
                onChange={(e) => {
                  const next = [...rows];
                  next[i] = {
                    ...r,
                    enabled: e.target.checked,
                    max_accounts: e.target.checked ? Math.max(1, r.max_accounts) : 0,
                  };
                  setRows(next);
                }}
              />
              <span className="w-24 capitalize">{r.broker}</span>
              {DATA_ONLY_BROKERS.includes(r.broker) && (
                <Chip tone="warn">data only</Chip>
              )}
              <input
                type="number"
                min={0}
                max={50}
                disabled={!r.enabled}
                value={r.max_accounts}
                onChange={(e) => {
                  const next = [...rows];
                  next[i] = { ...r, max_accounts: Number(e.target.value) };
                  setRows(next);
                }}
                className="w-20 rounded border border-border bg-surface px-2 py-1 text-sm disabled:opacity-40"
              />
              <span className="text-xs text-muted">
                {used} of {r.enabled ? r.max_accounts : 0} accounts in use
              </span>
              {overCap && <Chip tone="warn">over cap</Chip>}
            </div>
          );
        })}

        {stranding.length > 0 && (
          <div className="text-xs text-warn-700 dark:text-warn-400 border border-warn-200 dark:border-warn-400/30 rounded p-2.5">
            Saving will put existing connections outside this user's policy:{" "}
            {stranding.map((s) => s.broker).join(", ")}. Their connections are{" "}
            <strong>not deleted</strong> — the oldest allowed number stay usable,
            the newest can no longer trade (and a deployment using one will
            refuse to start). They can still rotate tokens and delete them.
          </div>
        )}
        {save.error && (
          <div className="text-sm text-down-600 dark:text-down-400">{apiErrorMessage(save.error)}</div>
        )}
      </div>
    </Card>
  );
}

function StrategyGrantsCard({
  user,
  all,
  onSaved,
}: {
  user: AdminUserDetail;
  all: { key: string; label: string }[];
  onSaved: () => void;
}) {
  const [selected, setSelected] = useState<string[]>(user.strategy_keys);
  useEffect(() => setSelected(user.strategy_keys), [user.strategy_keys]);

  const save = useMutation({
    mutationFn: () => setStrategyGrants(user.id, selected),
    onSuccess: onSaved,
  });

  const dirty =
    JSON.stringify([...selected].sort()) !==
    JSON.stringify([...user.strategy_keys].sort());
  const removing = user.strategy_keys.filter((k) => !selected.includes(k));

  return (
    <Card
      title="Strategy access"
      subtitle={`${selected.length} of ${all.length} granted.`}
      actions={
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSelected(all.map((s) => s.key))}
          >
            Select all
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected([])}>
            Clear
          </Button>
          <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="space-y-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
          {all.map((s) => (
            <label key={s.key} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selected.includes(s.key)}
                onChange={(e) =>
                  setSelected((prev) =>
                    e.target.checked
                      ? [...prev, s.key]
                      : prev.filter((k) => k !== s.key),
                  )
                }
              />
              <span className="truncate" title={s.label}>
                {s.label}
              </span>
            </label>
          ))}
        </div>

        {removing.length > 0 && (
          <div className="text-xs text-warn-700 dark:text-warn-400 border border-warn-200 dark:border-warn-400/30 rounded p-2.5">
            Revoking {removing.join(", ")}. Any deployment already running on
            these <strong>keeps running</strong> — abandoning a live position is
            worse than a few more minutes of access — but it cannot be restarted,
            and the user sees why on the deployment.
          </div>
        )}
        {save.error && (
          <div className="text-sm text-down-600 dark:text-down-400">{apiErrorMessage(save.error)}</div>
        )}
      </div>
    </Card>
  );
}
